import { ApolloError, useQuery } from '@apollo/client';

import {
  GET_LATEST_ASC_REPORT_DOCUMENT_EVENT,
  GetLatestAscReportDocumentEventQuery,
  GetLatestAscReportDocumentEventQueryVariables,
  PeriodName,
} from '@neo.tax/shared';

export type LatestAscReportDocumentEvent = NonNullable<
  GetLatestAscReportDocumentEventQuery['getLatestAscReportDocumentEvent']
>;

export type UseGetLatestAscReportDocumentEvent =
  | {
      status: 'loading';
      documentEvent: null;
      error: null;
      refetch: () => Promise<unknown>;
    }
  | {
      status: 'loaded';
      documentEvent: LatestAscReportDocumentEvent | null;
      error: null;
      refetch: () => Promise<unknown>;
    }
  | {
      status: 'error';
      documentEvent: null;
      error: ApolloError;
      refetch: () => Promise<unknown>;
    };

/**
 * Returns the most recent manually-generated ASC report for the given fiscal
 * year + period, or null if none has been generated.
 *
 * Cache eviction is driven by `useBackgroundOperations` watching for the
 * `GenerateAscReportOperation` to flip to 100% — no client-side polling here.
 */
export const useGetLatestAscReportDocumentEvent = (
  fiscalYearId: string,
  periodName: PeriodName,
): UseGetLatestAscReportDocumentEvent => {
  const { data, error, loading, refetch } = useQuery<
    GetLatestAscReportDocumentEventQuery,
    GetLatestAscReportDocumentEventQueryVariables
  >(GET_LATEST_ASC_REPORT_DOCUMENT_EVENT, {
    variables: { fiscalYearId, periodName },
    fetchPolicy: 'cache-and-network',
  });

  // Apollo's `useQuery` retains the previously-resolved `data` for a render or
  // two after `variables` change before the new fetch settles. Treat any event
  // whose `periodName` doesn't match the requested period as stale so the UI
  // doesn't briefly show the prior period's timestamp when the user switches
  // periods.
  const documentEvent = data?.getLatestAscReportDocumentEvent ?? null;
  const isForRequestedPeriod = documentEvent === null || documentEvent.periodName === periodName;

  if (error) {
    return { status: 'error', documentEvent: null, error, refetch };
  }
  if (loading && (!data || !isForRequestedPeriod)) {
    return { status: 'loading', documentEvent: null, error: null, refetch };
  }

  return {
    status: 'loaded',
    documentEvent: isForRequestedPeriod ? documentEvent : null,
    error: null,
    refetch,
  };
};
